import { useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { Term } from '../components/Term';

/** One labelled line inside a fact row. */
function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <span className="dp-muted">{label}</span> {children}
    </div>
  );
}

interface GrantedFeature {
  name: string;
  id: string;
  gates: ReactNode;
  note?: string;
}

/**
 * The features a Revvly platform admin can turn on for a workspace
 * (GRANTABLE_FEATURES in tenant.enum.ts). Order follows that list; assistant-led
 * onboarding is a rollout switch and is left out.
 */
const GRANTED: GrantedFeature[] = [
  {
    name: 'CRM',
    id: 'CRM',
    gates:
      'Companies, Contacts and Pipeline in the Customers menu. The CRM API behind them: pipelines and stages, leads, notes, documents, meetings, email logging, lifecycle stage and owner. CRM import, CRM email templates, booking intake routes, Gmail connection and email signatures, Calendly connection, contact suggestions, email thread visibility, and contacts on a client. The public API mutation createProspect. Library documents and the Library Documents screen are open to CRM or Support.',
    note: 'Tasks are available to every workspace and are not gated by CRM. The first time CRM is granted, a default pipeline is seeded.',
  },
  {
    name: 'Billing schedules',
    id: 'BILLING_SCHEDULE',
    gates:
      'The prepaid-term and deferred-activation presets on the subscription wizard, the pending-activation list on Renewals, and the Activate action.',
    note: 'Only the schedule surfaces are gated. Credit notes, typed invoice lines and cash-flow projections that skip invoiced-ahead periods work without it.',
  },
  {
    name: 'OKRs',
    id: 'OKR',
    gates:
      'The OKR API (cycles, objectives, key results, work links, alignment) and the OKR Cycle, My OKRs and Coverage entries under Strategy.',
    note: 'Granting it is the commercial step. A workspace admin must also turn the module on under Settings, Modules; until then the API answers that OKRs are not switched on for the account.',
  },
  {
    name: 'Email triage (Tier 1)',
    id: 'EMAIL_TIER1_TRIAGE',
    gates:
      'The hourly AI pass that classifies inbound email the first-stage rules escalated. No screen is gated.',
    note: 'A workspace is swept only if it carries this feature and Revvly has configured the triage model and a positive monthly budget. Either one missing means nothing runs.',
  },
  {
    name: 'Commitment extraction (Tier 2)',
    id: 'EMAIL_TIER2_EXTRACTION',
    gates:
      'The hourly AI pass that reads triaged email for commitments, which feed overdue-commitment situations. No screen is gated.',
    note: 'Independent of Tier 1, but it only sees messages Tier 1 has qualified. Also needs the extraction model and a positive monthly budget configured by Revvly.',
  },
  {
    name: 'Support',
    id: 'SUPPORT',
    gates:
      'The Support section (Inbox, End users, Knowledge, Metrics) and every Support API: tickets, settings, knowledge, imports, metrics, notifications, work-item links, the live stream and the sandbox. On the public API, createEndUserToken, upsertSupportUsers, the knowledge mutations and workItemLinks. Library documents (CRM or Support) and sharing a library link from a ticket.',
    note: 'The first time Support is granted, ticket types, workflows and settings are seeded.',
  },
  {
    name: 'Forms',
    id: 'FORMS',
    gates:
      'Library, Forms; the form builder API; the Assistant and MCP form tools; public hosted and embedded forms; the API-key mutation submitForm; and the runner for follow-up steps on each submission.',
    note: 'If a workspace loses Forms, its public form routes answer 404 and steps still waiting are held, not run.',
  },
  {
    name: 'Daily check-ins',
    id: 'CHECKINS',
    gates: (
      <>
        Settings, Check-ins and the check-in API behind it; the daily Slack direct messages, the
        answers and summary posted to each check-in&rsquo;s channel; the /revvly standup command;
        and the Assistant&rsquo;s check-in tools. Admins only. See{' '}
        <Link to="/docs/guides/daily-checkins">Run daily check-ins in Slack</Link>.
      </>
    ),
    note: 'Entitlement does not need a Slack connection, so a workspace can set check-ins up first, but nothing is sent until Slack is connected. A disconnected Slack pauses every check-in.',
  },
  {
    name: 'Tax',
    id: 'TAX',
    gates:
      'Settings, Tax; the Tax tab on Finance; the tax panel on draft invoices; the tax API (settings, registrations, rates, customer tax profiles, reports, ledger).',
    note: 'Granting is the entitlement. The workspace then switches tax on in its own tax settings, which is refused until an active registration exists. Nothing on an invoice changes before that.',
  },
  {
    name: 'Work Inbox',
    id: 'WORK_INBOX',
    gates: (
      <>
        Inbox at /inbox and its settings, the daily inbox digest notification preference, and the
        Assistant and MCP inbox tools. See <Link to="/docs/guides/work-inbox">Work Inbox</Link>.
      </>
    ),
    note: 'Each person connects their own Gmail mailbox, and nobody sees anyone else’s mail.',
  },
  {
    name: 'Drive client folders',
    id: 'DRIVE_FOLDERS',
    gates: (
      <>
        A Google Drive folder per client and prospect, kept in sync with the files Revvly holds: the
        client folders settings under Settings, Integrations (admins), the folder panel and Drive
        marks on a client&rsquo;s documents, and the paused-sync notification. See{' '}
        <Link to="/docs/guides/drive-folders">Client folders in Google Drive</Link>.
      </>
    ),
    note: 'Needs Google Drive connected with write access to do anything; entitlement does not require it.',
  },
];

interface StandardFeature {
  name: string;
  id: string;
  gates: string;
}

/** Features every regular workspace starts with (DEFAULT_REGULAR_FEATURES). */
const STANDARD: StandardFeature[] = [
  {
    name: 'Finance',
    id: 'FINANCE',
    gates:
      'Enforced in the API: payment reminders and reminder templates, invoice payments, credit notes and the accounting mailbox. In the dashboard: the Templates page and the payment-reminder entries in search.',
  },
  {
    name: 'Subscriptions',
    id: 'SUBSCRIPTIONS',
    gates: 'No backend check and no dashboard check found.',
  },
  {
    name: 'Organizations (Clients)',
    id: 'ORGANIZATIONS',
    gates: 'No backend check and no dashboard check found.',
  },
  {
    name: 'Analytics',
    id: 'ANALYTICS',
    gates:
      'No backend check found; the dashboard FeatureGate component names it but no screen depends on it.',
  },
  {
    name: 'Feature flags',
    id: 'FEATURE_FLAGS',
    gates: 'No backend check and no dashboard check found.',
  },
  {
    name: 'Product config',
    id: 'PRODUCT_CONFIG',
    gates: 'No backend check and no dashboard check found.',
  },
  {
    name: 'Market config (Regions)',
    id: 'MARKET_CONFIG',
    gates: 'No backend check and no dashboard check found.',
  },
  { name: 'Pricing', id: 'PRICING', gates: 'No backend check and no dashboard check found.' },
  { name: 'Import', id: 'IMPORT', gates: 'No backend check and no dashboard check found.' },
];

export function WorkspaceFeaturesReference() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Workspace features — Revvly Docs';
  }, []);

  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/reference">Reference</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Workspace features</span>
      </nav>

      <header className="dp-header">
        <span className="rv-eyebrow-mini">Reference</span>
        <h1>
          Workspace features and <em>what they unlock</em>.
        </h1>
        <p className="dp-oneliner">
          Every workspace feature, what it gates, who can turn it on, and what you see when it is
          off. Workspace features are separate from the product feature flags you attach to modules
          and bundles, which are covered in section 06.
        </p>
      </header>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">01</span>
          How workspace features work
        </h2>
        <div className="dp-fields-table">
          <div className="dp-field-row">
            <div className="dp-field-k">Where it is stored</div>
            <div className="dp-field-v">
              A list of features on your <Term id="tenant">Tenant</Term> (workspace). The dashboard
              reads the list for the workspace you have selected.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Who can grant</div>
            <div className="dp-field-v">
              Only a Revvly platform admin. The control is the Access and features card on the
              client record, and the API call behind it rejects anyone who is not a platform admin.
              A workspace admin cannot turn a feature on for their own workspace.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">What can be granted</div>
            <div className="dp-field-v">
              The features listed in section 02. Asking the API to grant any other feature is
              refused. Features a workspace already holds outside that list are left untouched when
              these are changed.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Platform workspace</div>
            <div className="dp-field-v">
              The Revvly platform workspace holds every grantable feature. That is reconciled on
              every boot.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Default set</div>
            <div className="dp-field-v">
              A regular workspace without a stored list is treated as holding the nine standard
              features in section 03. None of the grantable features is in the default set.
            </div>
          </div>
        </div>
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">02</span>
          Features Revvly grants
        </h2>
        <p className="dp-section-sub">
          Each of these is off until a platform admin turns it on. The name is the label on the
          grant card; the code is the value the API uses.
        </p>
        <div className="dp-fields-table">
          {GRANTED.map((f) => (
            <div className="dp-field-row" key={f.id}>
              <div className="dp-field-k">
                {f.name}
                <div className="dp-muted">{f.id}</div>
              </div>
              <div className="dp-field-v">
                <Fact label="Gates">{f.gates}</Fact>
                <Fact label="Granted by">Revvly platform admin only.</Fact>
                {f.note ? <Fact label="Note">{f.note}</Fact> : null}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">03</span>
          Standard features
        </h2>
        <p className="dp-section-sub">
          Regular workspaces start with these. They cannot be switched off from the grant card. Only
          Finance is checked in the code today; the rest are recorded on the workspace but no screen
          or API call depends on them.
        </p>
        <div className="dp-fields-table">
          {STANDARD.map((f) => (
            <div className="dp-field-row" key={f.id}>
              <div className="dp-field-k">
                {f.name}
                <div className="dp-muted">{f.id}</div>
              </div>
              <div className="dp-field-v">{f.gates}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">04</span>
          Structural and deprecated
        </h2>
        <div className="dp-fields-table">
          <div className="dp-field-row">
            <div className="dp-field-k">
              Tenant management
              <div className="dp-muted">TENANT_MANAGEMENT</div>
            </div>
            <div className="dp-field-v">
              Held by the Revvly platform workspace. Not grantable. No backend or dashboard check on
              this value was found.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              Incubators
              <div className="dp-muted">INCUBATORS</div>
            </div>
            <div className="dp-field-v">
              Not grantable. The Incubators menu entry is commented out in the dashboard and the
              incubator routes redirect to Dashboard, so the feature unlocks nothing you can see.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              User management
              <div className="dp-muted">USER_MANAGEMENT</div>
            </div>
            <div className="dp-field-v">
              Not grantable. The accelerator-specific Users menu entry is commented out. The Users
              page under Admin is shown to every workspace regardless of this value.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">
              Auto renewal
              <div className="dp-muted">AUTO_RENEWAL</div>
            </div>
            <div className="dp-field-v">
              Deprecated. It gates nothing. Renewals run for every active workspace; whether one
              subscription renews is decided by its own auto-renew setting. The value is kept only
              so older workspace records stay valid.
            </div>
          </div>
        </div>
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">05</span>
          What you see when a feature is off
        </h2>
        <div className="dp-fields-table">
          <div className="dp-field-row">
            <div className="dp-field-k">Menu and search</div>
            <div className="dp-field-v">
              The entry is not shown in the sidebar or in the command palette. A group with several
              entries, such as Library, shows only the ones you hold.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Typing the URL</div>
            <div className="dp-field-v">
              Guarded routes redirect to Dashboard. Settings, Tax and Settings, Check-ins fall back
              to Settings, General.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Calling the API</div>
            <div className="dp-field-v">
              Dashboard GraphQL operations answer 403 with: The &quot;CRM&quot; feature is not
              enabled for this tenant (the message names the feature). Operations open to either of
              two features list both. On the public API, the CRM, Forms and Support operations
              answer 403 FORBIDDEN with a message that says the product is not enabled for this
              tenant.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Public forms</div>
            <div className="dp-field-v">
              A workspace that loses Forms takes its public form routes offline (404) and holds
              pending follow-up steps.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Entitled but not switched on</div>
            <div className="dp-field-v">
              OKRs and Tax need a second step by the workspace after the grant. Until then the
              feature is available and the module reports that it is not switched on.
            </div>
          </div>
        </div>
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">06</span>
          Product feature flags are a different thing
        </h2>
        <p className="dp-section-sub">
          A <Term id="featureFlag">Feature Flag</Term> in the catalog is a capability you sell, such
          as advanced reporting. It describes what your customers have. It does not switch Revvly
          screens on or off for your own team.
        </p>
        <div className="dp-fields-table">
          <div className="dp-field-row">
            <div className="dp-field-k">Where</div>
            <div className="dp-field-v">Catalog, Feature Flags in the Admin menu.</div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Fields</div>
            <div className="dp-field-v">
              Name, key (the identifier your own application reads), description, optional category,
              and an active switch.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Entitlements</div>
            <div className="dp-field-v">
              Flags are attached to <Term id="module">Module</Term> tiers and to Bundles.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Per client</div>
            <div className="dp-field-v">
              A client&rsquo;s flag assignment records its source: bundle, module or manual. Manual
              assignments can enable or remove a single flag for one client.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Who edits</div>
            <div className="dp-field-v">
              Writes to the flag catalog and to a client&rsquo;s assignments require a workspace
              admin, not just membership.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Sync to your app</div>
            <div className="dp-field-v">
              API mutations can push a client&rsquo;s manual assignments to an external application
              and pull flags from it. The target is set by Revvly deployment configuration, not in
              workspace settings, and the current dashboard has no screen for it. Nothing in Revvly
              reads these flags to change what a screen shows.
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
